/** TanStack Query hooks for the candidate surface (Plan 03-05). */
import { useMutation, useQuery } from "@tanstack/react-query";

import * as api from "./api";

export function useConsentQuery(positionToken: string) {
  return useQuery({
    queryKey: ["candidate-consent", positionToken],
    queryFn: () => api.fetchConsent(positionToken),
    enabled: !!positionToken,
    retry: 1,
  });
}

export function usePostConsent() {
  return useMutation({
    mutationFn: ({
      positionToken,
      consentVersionId,
    }: {
      positionToken: string;
      consentVersionId: number;
    }) => api.postConsent(positionToken, consentVersionId),
    onSuccess: (data) => {
      api.setCandidateSessionToken(data.session_token);
    },
  });
}

export function useSessionStateQuery() {
  return useQuery({
    queryKey: ["candidate-session"],
    queryFn: api.fetchSessionState,
    retry: false,
    enabled: !!api.getCandidateSessionToken(),
  });
}

export function usePostPersonalInfo() {
  return useMutation({ mutationFn: api.postPersonalInfo });
}

// ---- Plan 03-06: Stage 1 question loop ----

/** Ordered question list (D-A). Immutable post-Activate so cache long; gated on Bearer. */
export function useQuestionsQuery() {
  return useQuery({
    queryKey: ["candidate-questions"],
    queryFn: api.fetchQuestions,
    retry: false,
    staleTime: Infinity, // snapshot is immutable post-Activate
    enabled: !!api.getCandidateSessionToken(),
  });
}

/** UPSERT draft answer (D1 + D10). Server is source of truth; no client cache invalidation. */
export function useDraftMutation() {
  return useMutation({ mutationFn: api.postDraft });
}

/** Fire-and-forget tab-switch (D4). retry: 0 + api.postTabSwitch already swallows errors. */
export function useTabSwitchMutation() {
  return useMutation({ mutationFn: api.postTabSwitch, retry: 0 });
}

/**
 * Finalize Stage 1 (D5). On success: persist reference_number to sessionStorage so
 * /done survives refresh (D-F — the Bearer token is dead server-side post-submit),
 * then clear the now-dead session token (D2 clear-on-submit).
 */
export function useSubmitMutation() {
  return useMutation({
    mutationFn: api.postSubmit,
    onSuccess: (data) => {
      api.setCandidateReferenceNumber(data.reference_number);
      api.clearCandidateSessionToken();
    },
  });
}
